<template>
    <div>
        <infinite-autocomplete
            :input-id="inputId"
            :key="key"
            v-model="selected"
            :disabled="disabled"
            :items="allItems"
            :loading="loading"
            :hasMore="hasMore"
            :item-text="itemText"
            :label="labelText"
            :placeholder="placeholder"
            :required="required"
            :autofocus="autofocus"
            :readonly="readonly"
            :hide-details="hideDetails"
            :dense="dense"
            :clearable="isClearable"
            :selected-key="selectedKey"
            :active-item="activeItem"
            :outlined="outlined"
            :flat="flat"
            :background-color="backgroundColor"
            :icon="icon"
            :error-messages="errorMessages"
            :menu-props="menuProps"
            :select-mode="selectMode"
            @menu-opened="onMenuOpened"
            @search-changed="onSearchChanged"
            @load-more="loadNextPage"
            @select="$emit('select', $event)"
            @input="inputChanged"
            ref="search"
        >
            <template v-if="$scopedSlots.selection" slot="selection" slot-scope="{ item }">
                <slot name="selection" :item="item" />
            </template>
            <template slot="item" slot-scope="{ item }">
                <slot name="item" :item="item">
                    <v-list-item-title>
                        {{ itemText(item) }}
                    </v-list-item-title>
                </slot>
            </template>
            <template slot="active-item" slot-scope="{ activeItem }">
                <div class="active">
                    <slot name="item" :item="activeItem">
                        {{ itemText(activeItem) }}
                    </slot>
                </div>
            </template>
        </infinite-autocomplete>
    </div>
</template>

<script>
import axios from "@/plugins/axios";

export default {
    name: "ItemSearch",
    props: {
        inputId: String,
        // The base URL to fetch items from.
        baseUrl: {
            type: String,
            required: true,
        },
        // Additional GET parameters to include in the API request.
        additionalParams: {
            type: Object,
            default: () => ({}),
        },
        // Whether the component is disabled.
        disabled: {
            type: Boolean,
            default: false,
        },
        // v-model binding (selected item ID).
        value: {
            type: [String, Number],
            default: null,
        },
        // Preloaded object for `value`. When it matches the selected id, skip GET /{id}/.
        initialItem: {
            type: Object,
            default: null,
        },
        // Label for the autocomplete.
        label: {
            type: String,
            default: "",
        },
        // Optional limit per request.
        limit: {
            type: Number,
            default: 20,
        },
        itemText: {
            type: Function,
            default: () => (item) => item?.name,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
        allowCreate: {
            type: Boolean,
            default: false,
        },
        createLabel: {
            type: String,
            default: "new entry",
        },
        hideDetails: {
            type: [Boolean, String],
            default: false,
        },
        dense: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: true,
        },
        selectedKey: {
            type: String,
            default: "id",
        },
        outlined: {
            type: Boolean,
            default: false,
        },
        icon: {
            type: String,
            default: null,
        },
        flat: {
            type: Boolean,
            default: false,
        },
        backgroundColor: {
            type: String,
            default: null,
        },
        errorMessages: {
            type: [String, Array],
            default: null,
        },
        /** When true, load the first page as soon as the component is mounted (no need to open the menu). */
        loadOnMount: {
            type: Boolean,
            default: false,
        },
        menuProps: {
            type: Object,
            default: () => ({}),
        },
        /** When true, child uses v-select (no search field) instead of v-autocomplete. */
        selectMode: {
            type: Boolean,
            default: false,
        },
        hideLabel: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            selected: this.value,
            items: [],
            loading: false,
            hasMore: true,
            offset: 0,
            key: Math.random(), // Random key to force re-render on baseUrl change
            search: "",
            fetchedOnce: false, // Flag to know if an initial fetch has occurred
            activeItem: null, // To store the details of the currently selected item
        };
    },
    computed: {
        allItems() {
            let items = [];
            if (this.allowCreate && !this.value) {
                items.push({
                    name: this.createLabel,
                    id: "new",
                });
            }
            if (
                this.activeItem &&
                !this.search &&
                !this.items.find((item) => item.id === this.activeItem.id)
            ) {
                items.push(this.activeItem);
            }
            return items.concat(this.items);
        },
        labelText() {
            if (this.hideLabel) {
                return "";
            }
            return this.label || this.$translate("select_item");
        },
        isClearable() {
            return this.clearable && !this.readonly && !this.disabled;
        },
    },
    watch: {
        value(newVal) {
            const normalized = newVal === "" ? null : newVal;
            this.selected = normalized;
            if (normalized == null) {
                this.activeItem = null;
                return;
            }
            this.hydrateActiveItem(normalized);
        },
        initialItem: {
            handler() {
                if (this.value == null || this.value === "") {
                    return;
                }
                this.hydrateActiveItem(this.value);
            },
        },
        baseUrl(newVal, oldVal) {
            if (newVal === oldVal) return;
            this.resetAndFetch();
        },
        additionalParams: {
            handler(newVal, oldVal) {
                if (JSON.stringify(newVal) === JSON.stringify(oldVal)) return;
                this.resetAndFetch();
            },
            deep: true,
            immediate: false,
        },
    },
    methods: {
        reset() {
            this.key = Math.random(); // Change key to force re-render
        },
        update() {
            this.fetchItems();
        },
        inputChanged(value) {
            if (value === "new") {
                this.$emit("create");
                this.selected = null;
                return;
            }
            // Clear must emit null (not undefined) so parents can persist an
            // explicit null FK in PUT/POST payloads.
            if (value == null || value === "") {
                this.selected = null;
                this.activeItem = null;
                this.$emit("input", null);
                return;
            }
            const object = this.allItems.find((item) => item.id == value);
            this.$emit("input", object || null);
        },
        resetAndFetch() {
            // Reset the state and clear fetchedOnce flag.
            // this.items = [];
            this.offset = 0;
            this.hasMore = true;
            this.fetchedOnce = false;
            // Optionally, you can fetch default entries when URL/params change.
            // For example, if you want to load defaults immediately, uncomment:
            // if (this.value) {
            //     this.fetchItemDetail(this.value);
            // }
            this.fetchItems();
        },
        onMenuOpened() {
            // When the menu opens, if no items have been loaded, fetch default entries.
            if (!this.fetchedOnce) {
                this.fetchItems();
            }
        },
        fetchItems() {
            this.loading = true;
            this.offset = 0;
            axios
                .get(this.baseUrl, {
                    params: {
                        search: this.search,
                        offset: this.offset,
                        limit: this.limit,
                        ...this.additionalParams,
                    },
                })
                .then((response) => {
                    this.items = response.data.results;
                    this.hasMore = !!response.data.next;
                    this.fetchedOnce = true;
                    this.$forceUpdate();
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        fetchMoreItems() {
            if (!this.hasMore) return;
            // Only fetch more if an initial fetch has occurred.
            if (!this.fetchedOnce) return;
            this.loading = true;
            this.offset += this.limit;
            axios
                .get(this.baseUrl, {
                    params: {
                        search: this.search,
                        offset: this.offset,
                        limit: this.limit,
                        ...this.additionalParams,
                    },
                })
                .then((response) => {
                    this.items = this.items.concat(response.data.results);
                    this.hasMore = !!response.data.next;
                    this.$forceUpdate();
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadNextPage() {
            // This method is called when the infinite-autocomplete emits load-more.
            // It calls fetchMoreItems only if an initial fetch has been made.
            if (this.fetchedOnce) {
                this.fetchMoreItems();
            } else {
                this.fetchItems();
            }
        },
        sameItemId(item, itemId) {
            if (item == null || itemId == null || itemId === "") {
                return false;
            }
            const key = this.selectedKey || "id";
            return item[key] == itemId;
        },
        hydrateActiveItem(itemId) {
            if (this.sameItemId(this.activeItem, itemId)) {
                return true;
            }
            if (this.sameItemId(this.initialItem, itemId)) {
                this.activeItem = this.initialItem;
                return true;
            }
            const fromItems = this.items.find((item) => item.id == itemId);
            if (fromItems) {
                this.activeItem = fromItems;
                return true;
            }
            this.fetchItemDetail(itemId);
            return false;
        },
        async fetchItemDetail(itemId) {
            try {
                const response = await axios.get(`${this.baseUrl}${itemId}/`);
                this.activeItem = response.data;
                this.$forceUpdate();
            } catch (error) {
                console.error("Error fetching detail:", error);
            }
        },
        onSearchChanged(newSearch) {
            if (!this.search && !newSearch) return; // No change in search term
            this.search = newSearch;
            this.resetAndFetch();
        },
        focus() {
            this.$refs.search.focus();
        },
        openMenu() {
            if (!this.fetchedOnce) {
                this.fetchItems();
            }
            this.$refs.search?.openMenu?.();
        },
    },
    mounted() {
        if (this.value) {
            this.selected = this.value;
            this.hydrateActiveItem(this.value);
        }
        if (this.loadOnMount) {
            this.fetchItems();
        }
    },
    i18n: {
        messages: {
            en: {
                select_item: "Select an item",
            },
            fr: {
                select_item: "Sélectionnez un élément",
            },
        },
    },
};
</script>

<style scoped>
/* Add any additional styling as needed */
</style>
